import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import {
  V1ClaimTokenResponse,
  V1GetTenantByHostnameResponse,
  V1TokenStatus,
  V1TokenType,
} from '../../core/interface';
import { CardTheme, CardType } from './components/card/card';
import { KeyStatusBannerComponent } from './components/key-status-banner/key-status-banner.component';
import { LockCardComponent } from './components/lock-card/lock-card.component';
import { ApiSafeService } from '../../core/service/api-safe.service';
import { FaviconService } from '../../core/service/favicon.service';
import { IdbKeyvalService } from '../../core/service/idb-keyval.service';
import { NetworkService } from '../../core/service/network.service';
import { PlatformService } from '../../core/service/platform.service';
import { SnackbarService } from '../../core/service/snackbar.service';
import { ThemeService } from '../../core/service/theme.service';
import { UnlockReportService } from '../../core/service/unlock-report.service';
import {
  BLE_NOT_SUPPORTED_MSG,
  CancelledError,
  WasmService,
} from '../../core/service/wasm.service';
import { getBrand, getBrandFaviconDataUri } from '../../core/utils/brand';
import { buildIntentUri } from '../../core/utils/intent-uri';
import { getTokenFromUrl, isValidToken } from '../../core/utils/token';
import { KEY_DURATION_MS, KeyStatus } from '../../models/types';
import { IglooLogoComponent } from '../../shared/components/igloo-logo/igloo-logo.component';
import { InstallBannerService } from '../../shared/components/install-banner/install-banner.service';

export type PageState =
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; message: string }
  | { status: 'expired' }
  | { status: 'offline' };

export interface UnlockPageTheme {
  img_url?: string;
  bg_color: string;
  label_color: string;
  loading_color: string;
  error_color: string;
  success_color: string;
}

function parseHex(hex: string): [number, number, number] {
  let h = (hex === 'white' ? '#ffffff' : hex === 'black' ? '#000000' : hex).replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function srgbLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function luminance(r: number, g: number, b: number): number {
  return 0.2126 * srgbLinear(r) + 0.7152 * srgbLinear(g) + 0.0722 * srgbLinear(b);
}

function contrast(l1: number, l2: number): number {
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

// Returns WCAG AA-compliant text color for `successHex` on a card with
// `color-mix(in srgb, successHex 8%, bgHex)` background. Darkens toward
// black when the original doesn't pass; returns as-is when it already does
// (handles dark-bg themes like Igloo correctly).
function computeSuccessTextColor(successHex: string, bgHex: string): string {
  const [sr, sg, sb] = parseHex(successHex);
  const [br, bg, bb] = parseHex(bgHex === 'transparent' ? '#ffffff' : bgHex);
  const cardL = luminance(0.08 * sr + 0.92 * br, 0.08 * sg + 0.92 * bg, 0.08 * sb + 0.92 * bb);
  if (contrast(cardL, luminance(sr, sg, sb)) >= 4.5) return successHex;
  for (let f = 0.85; f >= 0.05; f -= 0.05) {
    const r = sr * f,
      g = sg * f,
      b = sb * f;
    if (contrast(cardL, luminance(r, g, b)) >= 4.5) {
      return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
    }
  }
  return '#000000';
}

const BLE_NOT_SUPPORTED_ERROR = BLE_NOT_SUPPORTED_MSG;

// Matches gRPC server errors where stale IDB/localStorage is the likely cause.
// WASM error messages must stay in sync; any change to wasm.service toFriendlyMessage
// for the server path requires updating this list.
function isServerErrorMessage(msg: string): boolean {
  return msg === 'Server error.' || msg.includes('rpc error') || msg.includes('Internal error');
}

@Component({
  selector: 'app-unlock-page',
  standalone: true,
  imports: [LockCardComponent, KeyStatusBannerComponent, IglooLogoComponent],
  templateUrl: './unlock.page.html',
  styleUrl: './unlock.page.css',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ opacity: 0, height: 0, marginBottom: 0 }),
        animate('200ms ease-out', style({ opacity: 1, height: '*', marginBottom: 16 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, height: 0, marginBottom: 0 })),
      ]),
    ]),
    trigger('fadeUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(100%)' })),
      ]),
    ]),
  ],
  host: {
    class: 'flex flex-col flex-1 h-full min-h-0 w-full relative',
    '[style.--th-bg]': 'unlockPageTheme().bg_color',
    '[style.--th-label]': 'unlockPageTheme().label_color',
    '[style.--th-loading]': 'unlockPageTheme().loading_color',
    '[style.--th-error]': 'unlockPageTheme().error_color',
    '[style.--th-success]': 'unlockPageTheme().success_color',
    '[style.--th-success-text]': 'successTextColor()',
    '[style.--th-card-bg]': 'cardTheme().bg_color',
    '[style.--th-card-btn]': 'cardTheme().btn_color',
    '[style.--th-date]': 'cardTheme().date_color',
    '[style.--th-device-name]': 'cardTheme().device_name_color',
  },
})
export class UnlockPage implements OnInit, OnDestroy {
  private readonly apiSafeService = inject(ApiSafeService);
  private readonly snackBarService = inject(SnackbarService);
  private readonly idbKeyvalService = inject(IdbKeyvalService);
  private readonly faviconService = inject(FaviconService);
  private readonly installBannerService = inject(InstallBannerService);
  private readonly wasmService = inject(WasmService);
  private readonly unlockReportService = inject(UnlockReportService);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);
  public readonly themeService = inject(ThemeService);
  private readonly networkService = inject(NetworkService);
  readonly platformService = inject(PlatformService);
  protected readonly brand = getBrand();

  private destroyed = false;
  protected readonly now = signal(Date.now());
  private nowIntervalId?: ReturnType<typeof setInterval>;
  private reclaiming = false;

  private readonly onOnline = (): void => {
    void this.getToken();
  };

  private readonly onOffline = (): void => {
    this.pageState.set({ status: 'offline' });
    void this.getToken();
  };

  dataDevice = signal<CardType[]>([]);
  isAnyUnlocking = signal<boolean>(false);
  readonly showUnlockServerError = signal(false);
  readonly headerScrolled = signal(false);
  protected readonly showNoBleFallback = signal(false);

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly stickyHeader = viewChild<ElementRef<HTMLElement>>('stickyHeader');
  private readonly lockList = viewChild<ElementRef<HTMLElement>>('lockList');
  // viewChild resolves to the first match, i.e. the top lock card.
  private readonly firstLockCard = viewChild<ElementRef<HTMLElement>>('lockCard');
  private readonly isLandscape = signal(false);
  private landscapeQuery?: MediaQueryList;
  private readonly lockCardFits = signal(true);

  private readonly onOrientationChange = (e: MediaQueryListEvent): void => {
    this.isLandscape.set(e.matches);
  };

  // Phones in landscape can end up with the sticky header (and install banner) eating the
  // viewport so no lock card fits. Tablets have room, so they never trip it.
  readonly showRotatePrompt = computed(
    () =>
      (this.platformService.isAndroid || this.platformService.isIos) &&
      this.isLandscape() &&
      !this.lockCardFits(),
  );

  private readonly lockCardFitObserver = effect((onCleanup) => {
    const root = this.scroller()?.nativeElement;
    const header = this.stickyHeader()?.nativeElement;
    // No cards (empty/expired message) → the message itself must fit.
    const card = (this.firstLockCard() ?? this.lockList())?.nativeElement;
    if (!root || !header || !card || typeof ResizeObserver === 'undefined') return;

    // The space under the sticky header must fit one whole card. Scroll position and lock
    // count don't matter: with 10 locks any card can be scrolled into that space, so an
    // IntersectionObserver on a card would wrongly fire as soon as the user scrolls past it.
    const measure = (): void => {
      this.lockCardFits.set(root.clientHeight - header.offsetHeight >= card.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    ro.observe(header);
    ro.observe(card);
    onCleanup(() => ro.disconnect());
  });

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.headerScrolled.set(el.scrollTop > 0);
    this.installBannerService.hasScrollableContent.set(
      el.scrollHeight - el.scrollTop - el.clientHeight > 1,
    );
  }
  private readonly claimReceivedAt = signal<number | null>(null);
  readonly pageState = signal<PageState>(
    navigator.onLine ? { status: 'loading' } : { status: 'offline' },
  );
  currentPlatform = signal('');

  protected readonly iglooPageTheme: UnlockPageTheme = {
    img_url: 'assets/img/igloo.png',
    bg_color: 'black',
    label_color: '#d9d9d9',
    loading_color: '#0cc8a0',
    error_color: '#ff5a5f',
    success_color: '#0cc8a0',
  };

  protected readonly iglooCardTheme: CardTheme = {
    bg_color: '#282828',
    btn_color: '#0cc8a0',
    label_color: '#4e4e4e',
    date_color: '#d7d7d7',
    device_name_color: '#0cc8a0',
  };

  unlockPageTheme = signal<UnlockPageTheme>({
    img_url: 'assets/img/igloo.png',
    bg_color: 'white',
    label_color: '#717171',
    loading_color: '#0cc8a0',
    error_color: '#ff5a5f',
    success_color: '#0cc8a0',
  });
  readonly successTextColor = computed(() =>
    computeSuccessTextColor(this.unlockPageTheme().success_color, this.unlockPageTheme().bg_color),
  );
  cardTheme = signal<CardTheme>(this.iglooCardTheme);
  isEnterprise = signal(false);
  imageLogo = signal('');

  public readonly isOnline = this.networkService.isOnline;

  public readonly isOfflineEnabled = computed(() => {
    return this.dataDevice().some(
      (d) => d.token_type === 'TOKEN_TYPE_OFFLINE' || d.token_type === 'TOKEN_TYPE_HYBRID',
    );
  });

  public readonly validPeriodStart = computed(() => {
    const devices = this.dataDevice();
    return devices.length > 0 ? new Date(devices[0]!.start_date) : new Date();
  });

  public readonly validPeriodEnd = computed(() => {
    const devices = this.dataDevice();
    return devices.length > 0 ? new Date(devices[0]!.end_date) : new Date();
  });

  public readonly isWithinValidPeriod = computed(() => {
    if (this.dataDevice().length === 0) return false;
    const now = this.now();
    return now >= this.validPeriodStart().getTime() && now <= this.validPeriodEnd().getTime();
  });

  // Floors to match the Android app: 1d 22h -> "1d", under a day -> hours.
  public readonly timeRemaining = computed(() => {
    if (!this.isWithinValidPeriod()) return '';
    const hours = Math.floor((this.validPeriodEnd().getTime() - this.now()) / (1000 * 60 * 60));
    return hours >= 24 ? `${Math.floor(hours / 24)}d` : `${hours}h`;
  });

  public readonly digitalKey = computed(() => {
    const devices = this.dataDevice();
    if (devices.length === 0 || !this.isOfflineEnabled()) return null;
    const receivedAt = this.claimReceivedAt();
    if (!receivedAt) return null;
    const expiryStr = devices[0]?.guest_key_expires_at;
    const expiresAt = expiryStr ? new Date(expiryStr).getTime() : receivedAt + KEY_DURATION_MS;
    return { value: 'ACTIVE-TOKEN', fetchedAt: receivedAt, expiresAt };
  });

  public readonly keyStatus = computed<KeyStatus>(() => {
    const devices = this.dataDevice();
    if (devices.length === 0 || !this.isOfflineEnabled()) return 'none';
    const keyExpiry = devices[0]?.guest_key_expires_at;
    if (!keyExpiry) {
      // Fallback for older responses without an explicit expiry.
      const key = this.digitalKey();
      if (!key) return 'none';
      return this.now() < key.expiresAt ? 'valid' : 'expired';
    }
    return this.now() < new Date(keyExpiry).getTime() ? 'valid' : 'expired';
  });

  public readonly cardMetaBg = computed(() =>
    this.themeService.hexAlpha(this.unlockPageTheme().label_color, 0.06),
  );
  public readonly cardMetaBorder = computed(() =>
    this.themeService.hexAlpha(this.unlockPageTheme().label_color, 0.12),
  );

  public handleFetchKey(): void {
    window.location.reload();
  }

  public formatDate(date: Date): string {
    if (isNaN(date.getTime())) return 'Invalid Date';
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const datePartNoWrap = datePart.replace(/ /g, '\u00A0');
    const timePartNoWrap = timePart.replace(/ /g, '\u00A0');
    return `${datePartNoWrap}\u00A0-\u00A0${timePartNoWrap}`;
  }

  public formatTime(timestamp: number | undefined): string {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString();
  }

  constructor() {
    effect(() => {
      const key = this.digitalKey();
      if (!key || !this.isOnline() || this.reclaiming) return;
      const remaining = key.expiresAt - this.now();
      if (remaining > 5000) return;
      this.reclaiming = true;
      const token = this.idbKeyvalService.currentToken();
      if (token) void this.getDevice(token, true).finally(() => (this.reclaiming = false));
    });
  }

  ngOnInit(): void {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    this.landscapeQuery = window.matchMedia?.('(orientation: landscape)');
    this.isLandscape.set(!!this.landscapeQuery?.matches);
    this.landscapeQuery?.addEventListener('change', this.onOrientationChange);
    this.nowIntervalId = setInterval(() => this.now.set(Date.now()), 1000);

    this.pageState.set({ status: 'loading' });

    const token = getTokenFromUrl();
    if (
      this.platformService.isAndroid &&
      isValidToken(token) &&
      !sessionStorage.getItem('androidIntentFired')
    ) {
      sessionStorage.setItem('androidIntentFired', '1');
      window.location.href = buildIntentUri(token, this.brand);
      return;
    }

    void this.wasmService.ensureInitialized().then((result) => {
      if (this.destroyed) return;
      if (result.ok) {
        void this.getToken();
      } else {
        this.pageState.set({ status: 'error', message: result.error.message });
        this.installBannerService.pageLoaded.set(true);
      }
    });

    // Draining is driven by the claim path: app open and every reconnect re-claim
    // refresh the bearer and drain queued reports. The SW Background Sync handles
    // draining across sessions when the app isn't open. Server dedup makes an
    // overlapping app+SW drain a harmless no-op.
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    this.landscapeQuery?.removeEventListener('change', this.onOrientationChange);
    if (this.nowIntervalId) clearInterval(this.nowIntervalId);
  }

  async getToken(): Promise<void> {
    const pathId = getTokenFromUrl();
    if (isValidToken(pathId)) {
      await this.idbKeyvalService.updateDataToken(pathId);
      void this.getDevice(pathId);
      void this.getTenant();
    } else {
      await this.idbKeyvalService.getDataToken();
      if (this.idbKeyvalService.currentToken()) {
        window.history.replaceState({}, '', `/${this.idbKeyvalService.currentToken()}`);
        void this.getDevice(this.idbKeyvalService.currentToken()!);
        void this.getTenant();
      }
    }
  }

  async getTenant(): Promise<void> {
    await this.idbKeyvalService.getDataTheme();
    const key = this.idbKeyvalService.currentDataTheme();
    if (!this.networkService.isOnline()) {
      if (key) {
        this.convertTheme(key);
        this.imageLogo.set(key.tenant?.company_logo ?? '');
      } else {
        this.applyDefaultTheme();
      }
      return;
    }

    if (key) {
      this.convertTheme(key);
    }

    const result = await this.apiSafeService.getTenantByHostname(window.location.hostname);
    if (result.ok) {
      this.imageLogo.set(result.value.tenant?.company_logo ?? '');
      this.convertTheme(result.value);
      void this.idbKeyvalService.updateDataTheme(result.value);
    } else if (!key) {
      this.applyDefaultTheme();
    }
  }

  private applyDefaultTheme(): void {
    this.unlockPageTheme.set(this.iglooPageTheme);
    this.cardTheme.set(this.iglooCardTheme);
    this.faviconService.setFavicon(getBrandFaviconDataUri(this.brand));
  }

  injectCustomCss(css: string): void {
    const head = document.getElementsByTagName('head')[0];
    const styleId = 'tenant-custom-css';
    let style = document.getElementById(styleId) as HTMLStyleElement;
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      if (head) head.appendChild(style);
    }
    style.textContent = css;
  }

  convertTheme(dataTheme: V1GetTenantByHostnameResponse): void {
    if (!dataTheme.tenant) return;
    const t = dataTheme.tenant;
    if (t.color_theme) {
      const theme = t.color_theme;
      this.unlockPageTheme.set({
        img_url: t.company_logo || 'assets/img/igloo.png',
        bg_color: theme.background_color || 'white',
        label_color: theme.label_color || '#717171',
        loading_color: theme.loading_color || '#0cc8a0',
        error_color: theme.error_color || '#ff5a5f',
        success_color: theme.success_color || '#10b981',
      });
      this.cardTheme.set({
        bg_color: theme.card_bg_color || 'transparent',
        btn_color: theme.card_btn_color || '#0cc8a0',
        label_color: theme.label_color || '#717171',
        date_color: theme.date_color || '#000000',
        device_name_color: theme.device_name_color || '#0cc8a0',
      });
      this.currentPlatform.set(t.platform_id || 'web');
      this.installBannerService.bannerTheme.set({
        bg_color: theme.background_color || '#ee2a24',
        btn_color: theme.card_btn_color || 'white',
        text_color: theme.label_color || 'white',
      });
    } else {
      this.unlockPageTheme.set(this.iglooPageTheme);
      this.cardTheme.set(this.iglooCardTheme);
    }
    this.installBannerService.pageLoaded.set(true);
    if (t.favicon) this.faviconService.setFavicon(t.favicon);
    if (t.tier === 'TENANT_TIER_ENTERPRISE' && t.custom_css) {
      this.isEnterprise.set(true);
      this.injectCustomCss(t.custom_css);
    }
  }

  async getDevice(token: string, silent = false): Promise<void> {
    await this.idbKeyvalService.getDataDevice();
    const cachedReceivedAt = await this.idbKeyvalService.getClaimReceivedAt();
    this.claimReceivedAt.set(cachedReceivedAt);

    const key = this.idbKeyvalService.currentDataDevice();
    if (!this.networkService.isOnline()) {
      const cachedTokenString = key?.token?.token_string;
      const cacheMatchesToken = !cachedTokenString || cachedTokenString === token;
      if (key && cacheMatchesToken) {
        const accessState = this.classifyOfflineAccessWindow(key);
        if (accessState === 'expired') {
          this.pageState.set({ status: 'expired' });
        } else if (accessState === 'not-started') {
          this.pageState.set({ status: 'error', message: "Your access hasn't started yet." });
        } else {
          this.pageState.set({ status: 'success' });
          this.convertDataDevice(key);
        }
      } else {
        this.pageState.set({ status: 'error', message: 'Error fetching device data' });
      }
      return;
    }
    if (!silent) {
      if (key) {
        // Cached data available: render immediately, API will update when it responds.
        this.pageState.set({ status: 'success' });
        this.convertDataDevice(key);
      } else {
        this.pageState.set({ status: 'loading' });
      }
    }
    const result = await this.apiSafeService.claimToken(token);
    if (result.ok) {
      const nonClaimableState = this.classifyTokenStatus(result.value.token?.status);
      if (nonClaimableState) {
        if (result.value.token?.status === V1TokenStatus.TokenStatusRevoked) {
          // Revocation must invalidate the cached offline key so it cannot be
          // reused after the device goes offline again.
          await this.idbKeyvalService.clearAll();
          this.dataDevice.set([]);
        }
        this.pageState.set(nonClaimableState);
        return;
      }
      const receivedAt = Date.now();
      this.claimReceivedAt.set(receivedAt);
      void this.idbKeyvalService.updateClaimReceivedAt(receivedAt);
      this.pageState.set({ status: 'success' });
      this.convertDataDevice(result.value);
      void this.idbKeyvalService.updateDataDevice(result.value);
      if (result.value.access_token) {
        // Re-issue the shared unlock-report bearer (refreshed on every reconnect
        // re-claim), then drain queued offline reports with the live bearer.
        const accessToken = result.value.access_token;
        void this.unlockReportService
          .setBearer(accessToken)
          .then(() => this.unlockReportService.drain());
      }
    } else {
      // cachedReceivedAt is already set on the signal above for the error fallback path
      if (!silent) {
        this.handleGetDeviceError(result.error, key, token);
      } else {
        const { state } = this.classifyClaimError(result.error.message ?? '');
        if (state === 'expired' || state === 'revoked') {
          this.handleGetDeviceError(result.error, key, token);
        }
      }
    }
  }

  private handleGetDeviceError(
    error: Error,
    key: V1ClaimTokenResponse | null,
    token: string,
  ): void {
    const { message, isHard, state } = this.classifyClaimError(error.message ?? '');

    if (state === 'expired') {
      this.dataDevice.set([]);
      this.pageState.set({ status: 'expired' });
      return;
    }

    const cachedTokenString = key?.token?.token_string;
    const cacheMatchesToken = !cachedTokenString || cachedTokenString === token;

    if (isHard || !key || !cacheMatchesToken) {
      this.pageState.set({ status: 'error', message });
      return;
    }

    // API unreachable — update connectivity state so the rest of the page
    // (status pill, banner, WASM pre-warm) behaves correctly for offline.
    // Resets automatically when the browser fires the 'online' event.
    this.networkService.setOffline();
    this.pageState.set({ status: 'success' });
    this.convertDataDevice(key);
  }

  private classifyOfflineAccessWindow(
    cachedDevice: V1ClaimTokenResponse,
  ): 'valid' | 'expired' | 'not-started' {
    const token = cachedDevice.token;
    const now = Date.now();
    if (token?.expires_at && now > new Date(token.expires_at).getTime()) return 'expired';
    if (token?.access_window_end && now > new Date(token.access_window_end).getTime())
      return 'expired';
    if (token?.access_window_start && now < new Date(token.access_window_start).getTime())
      return 'not-started';
    const keyExpiry = cachedDevice.devices?.[0]?.guest_key_expires_at;
    if (keyExpiry && now > new Date(keyExpiry).getTime()) return 'expired';
    return 'valid';
  }

  private classifyTokenStatus(status: V1TokenStatus | undefined): PageState | null {
    switch (status) {
      case V1TokenStatus.TokenStatusExpired:
      case V1TokenStatus.TokenStatusAccessWindowEnded:
        return { status: 'expired' };
      case V1TokenStatus.TokenStatusNotYetActive:
        return { status: 'error', message: "Your access hasn't started yet." };
      case V1TokenStatus.TokenStatusRevoked:
        return { status: 'error', message: 'This link has been revoked.' };
      default:
        return null;
    }
  }

  private classifyClaimError(apiMessage: string): {
    message: string;
    isHard: boolean;
    state?: 'expired';
  } {
    if (apiMessage === 'token has expired' || apiMessage.includes('after access window end')) {
      return { message: '', isHard: true, state: 'expired' };
    }
    if (apiMessage.includes('before access window start')) {
      return { message: "Your access hasn't started yet.", isHard: true };
    }
    if (apiMessage.includes('cannot be claimed from this domain')) {
      return { message: "This link can't be used on this site.", isHard: true };
    }
    if (apiMessage === 'token is not claimable') {
      return { message: 'This link has been revoked.', isHard: true };
    }
    if (apiMessage === 'not found') {
      return { message: "This link doesn't exist. Check the URL and try again.", isHard: true };
    }
    return { message: "Couldn't load your access. Please try again.", isHard: false };
  }

  convertDataDevice(dataDevice: V1ClaimTokenResponse): void {
    const devices = dataDevice.devices ?? [];

    const hasOffline =
      dataDevice.token?.type === 'TOKEN_TYPE_OFFLINE' ||
      dataDevice.token?.type === 'TOKEN_TYPE_HYBRID';
    this.installBannerService.isOfflineToken.set(hasOffline);

    this.dataDevice.set(
      devices.map((device) => ({
        bluetooth_id: device.bluetooth_id ?? '',
        device_name: device.device_name ?? '',
        guest_key: device.guest_key ?? '',
        isLoading: false,
        isDisabled: false,
        start_date: dataDevice.token?.claimed_at ?? '',
        end_date: dataDevice.token?.expires_at ?? '',
        token_type: dataDevice.token?.type,
        guest_key_expires_at: device.guest_key_expires_at ?? '',
      })),
    );

    if (dataDevice.token?.type) {
      void this.wasmService.ensureInitialized(dataDevice.token.type as V1TokenType);
    }
  }

  handleClick = async (event: CardType): Promise<'success' | 'error' | 'cancelled'> => {
    this.isAnyUnlocking.set(true);
    this.showUnlockServerError.set(false);
    try {
      const unlock = await this.wasmService.handleUnlock(
        event.bluetooth_id,
        event.guest_key,
        event.token_type as V1TokenType,
        `${this.idbKeyvalService.currentToken()}`,
      );

      if (unlock.ok) {
        // Online unlocks are reported by the backend itself (it knows when the
        // unlock completed). Only the offline path needs a client-side report.
        if (!unlock.value.online) {
          // handleUnlock resolves when the BLE unlock command completes, so now is that time.
          const unlockedAt = Date.now();
          const tokenString = this.idbKeyvalService.currentToken();
          if (tokenString) {
            void this.unlockReportService.report({
              tokenString,
              bluetoothId: event.bluetooth_id,
              unlockedAt,
            });
          }
        }
        this.snackBarService.openSnackbarSuccess(unlock.value.message);
        return 'success';
      } else if (unlock.error instanceof CancelledError) {
        return 'cancelled';
      } else {
        const msg = unlock.error.message;
        const isOnlinePath =
          event.token_type === V1TokenType.TokenTypeOnline ||
          event.token_type === V1TokenType.TokenTypeHybrid;
        if (msg === BLE_NOT_SUPPORTED_ERROR) {
          this.snackBarService.openSnackbarError(msg);
          this.showNoBleFallback.set(true);
        } else if (isOnlinePath && isServerErrorMessage(msg)) {
          this.showUnlockServerError.set(true);
        } else {
          this.snackBarService.openSnackbarError(msg);
        }
        return 'error';
      }
    } finally {
      this.isAnyUnlocking.set(false);
    }
  };

  openInApp(): void {
    const token = getTokenFromUrl();
    this.platformService.openApp(token, this.brand);
  }

  async clearSavedDataAndRetry(): Promise<void> {
    try {
      await this.idbKeyvalService.clearAll();
      localStorage.removeItem('digital_key_v1');
      window.location.reload();
    } catch (e) {
      console.error('Failed to clear saved data:', e);
      this.snackBarService.openSnackbarError(
        'Could not clear saved data. Try clearing site data from your browser settings.',
      );
    }
  }
}
